import React from "react";
import { Box, Typography } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";

interface ErrorStateProps {
  /** Headline shown to the user. */
  title?: string;
  /** Supporting line under the title. */
  description?: string;
  /** When provided, renders a Retry button wired to this handler. */
  onRetry?: () => void;
  /** Retry button label. */
  retryLabel?: string;
}

/**
 * Generic load-failure state with an optional Retry action. Use when a data
 * fetch fails and the user needs a way to recover without reloading the page.
 *
 * @example
 * <ErrorState onRetry={refetch} />
 */
const ErrorState: React.FC<ErrorStateProps> = ({
  title = "Something went wrong",
  description = "We couldn't load this content. Please try again.",
  onRetry,
  retryLabel = "Retry",
}) => (
  <Stack
    gap="var(--space-4)"
    sx={{
      alignItems: "center",
      justifyContent: "center",
      padding: "var(--space-9) var(--space-5)",
      textAlign: "center",
    }}
  >
    <Typography variant="h5" component="h4">
      {title}
    </Typography>
    <Typography
      variant="body1"
      sx={{ color: "var(--color-text-tertiary)", maxWidth: 360 }}
    >
      {description}
    </Typography>
    {onRetry && (
      <Box
        component="button"
        type="button"
        onClick={onRetry}
        sx={{
          fontFamily: "var(--type-family-sans)",
          fontSize: "var(--type-size-sm)",
          fontWeight: "var(--type-weight-medium)",
          lineHeight: "var(--type-line-height-tight)",
          marginTop: "var(--space-2)",
          padding: "var(--space-3) var(--space-5)",
          border: "var(--border-width-1) solid var(--color-border)",
          borderRadius: "var(--radius-md)",
          backgroundColor: "var(--color-bg-surface)",
          color: "var(--color-text-primary)",
          cursor: "pointer",
          transition: "background-color 0.15s ease",
          "&:hover": {
            backgroundColor: "var(--color-bg-elevated)",
          },
        }}
      >
        {retryLabel}
      </Box>
    )}
  </Stack>
);

export default ErrorState;
